import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  applyManapoolBulkPrice,
  dryRunManapoolBulkPrice,
  extractManapoolBulkJobId,
  extractManapoolBulkJobStatus,
  getManapoolBulkPriceDefaults,
  getManapoolBulkPriceExportRows,
  getManapoolBulkPriceJob,
  isManapoolBulkJobCompleted,
  isManapoolBulkJobFailed,
  previewManapoolBulkPrice,
  syncManapoolBulkPriceFromExport,
  type ManapoolBulkPriceDefaults,
  type ManapoolBulkPriceExportSyncResult,
  type ManapoolBulkPricePreviewResult,
} from '../../api/pricing';
import { colors, spacing, typography, typographyFlat, borderRadius } from '../../styles/tokens';
import { formatInstant } from '../../utils/datetime';
import {
  extractPreviewItems,
  extractPreviewMeta,
  formatCents,
  formatSignedCents,
  itemLabel,
  summarizePreviewItems,
  topMovers,
  type BulkPricePreviewItem,
} from './bulkPricePreview';

type Phase =
  | 'idle'
  | 'loading_defaults'
  | 'previewing'
  | 'preview_ready'
  | 'loading_full_detail'
  | 'applying'
  | 'polling'
  | 'syncing'
  | 'done'
  | 'error';

function formatApiError(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { detail?: unknown } } }).response;
    const detail = response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((item) =>
          typeof item === 'object' && item && 'msg' in item
            ? String((item as { msg: unknown }).msg)
            : String(item),
        )
        .join('; ');
    }
  }
  return err instanceof Error ? err.message : 'Request failed';
}

function changeColor(cents: number): string {
  if (cents > 0) return colors.success;
  if (cents < 0) return colors.error;
  return colors.textSecondary;
}

const POLL_MS = 3000;
const MAX_POLLS = 120;

/**
 * Canonical Phase 1 reprice UI: Manapool bulk-price preview → confirm →
 * poll → sync-from-export (pricing.updated, Manapool echo skipped).
 */
export function ManapoolBulkRepricePanel() {
  const [phase, setPhase] = useState<Phase>('loading_defaults');
  const [defaults, setDefaults] = useState<ManapoolBulkPriceDefaults | null>(null);
  const [preview, setPreview] = useState<ManapoolBulkPricePreviewResult | null>(null);
  const [fullDetailItems, setFullDetailItems] = useState<BulkPricePreviewItem[] | null>(null);
  const [fullDetailJobId, setFullDetailJobId] = useState<string | null>(null);
  const [fullDetailSource, setFullDetailSource] = useState<'sample' | 'job' | 'export'>('sample');
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string>('');
  const [syncResult, setSyncResult] = useState<ManapoolBulkPriceExportSyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sampleItems = useMemo(() => {
    if (!preview) return [] as BulkPricePreviewItem[];
    const nestedItems = extractPreviewItems(preview.preview);
    return nestedItems.length > 0 ? nestedItems : extractPreviewItems(preview);
  }, [preview]);

  const activeItems = fullDetailItems ?? sampleItems;

  const previewSummary = useMemo(() => {
    if (activeItems.length === 0) return null;
    return summarizePreviewItems(activeItems);
  }, [activeItems]);

  const previewMeta = useMemo(() => {
    if (!preview) return null;
    const nested = extractPreviewMeta(preview.preview);
    const full = extractPreviewMeta(preview);
    return {
      totalCount: nested.totalCount ?? full.totalCount,
      previewCount: nested.previewCount ?? full.previewCount,
      skippedPreviewCount: nested.skippedPreviewCount ?? full.skippedPreviewCount,
      increases: nested.increases ?? full.increases,
      decreases: nested.decreases ?? full.decreases,
      unchanged: nested.unchanged ?? full.unchanged,
      successfulItems: nested.successfulItems ?? full.successfulItems,
      skippedItems: nested.skippedItems ?? full.skippedItems,
      totalValueChangeCents: nested.totalValueChangeCents ?? full.totalValueChangeCents,
    };
  }, [preview]);

  const movers = useMemo(
    () => (previewSummary ? topMovers(previewSummary.items) : []),
    [previewSummary],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getManapoolBulkPriceDefaults();
        if (!cancelled) {
          setDefaults(data);
          setPhase('idle');
        }
      } catch (err) {
        if (!cancelled) {
          setError(formatApiError(err));
          setPhase('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const runPreview = useCallback(async () => {
    setPhase('previewing');
    setError(null);
    setPreview(null);
    setFullDetailItems(null);
    setFullDetailJobId(null);
    setFullDetailSource('sample');
    setJobId(null);
    setJobStatus('');
    setSyncResult(null);
    try {
      const result = await previewManapoolBulkPrice({});
      setPreview(result);
      setPhase('preview_ready');
    } catch (err) {
      setError(formatApiError(err));
      setPhase('error');
    }
  }, []);

  const runLoadFullDetail = useCallback(async () => {
    setPhase('loading_full_detail');
    setError(null);
    setFullDetailItems(null);
    setFullDetailJobId(null);
    try {
      const dryRun = await dryRunManapoolBulkPrice({});
      const id = extractManapoolBulkJobId(dryRun);
      if (!id) {
        throw new Error('Manapool dry-run succeeded but returned no jobId');
      }
      setFullDetailJobId(id);

      let status = '';
      let lastJob: Record<string, unknown> = {};
      for (let i = 0; i < MAX_POLLS; i += 1) {
        const jobPayload = await getManapoolBulkPriceJob(id);
        lastJob = jobPayload as Record<string, unknown>;
        status = extractManapoolBulkJobStatus(jobPayload);
        setJobStatus(status);
        if (isManapoolBulkJobCompleted(status)) break;
        if (isManapoolBulkJobFailed(status)) {
          throw new Error(`Manapool dry-run job failed with status ${status || 'unknown'}`);
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_MS));
      }
      if (!isManapoolBulkJobCompleted(status)) {
        throw new Error(
          `Timed out waiting for Manapool dry-run job ${id} (last status: ${status || 'unknown'})`,
        );
      }

      // Prefer item rows on the job payload if Manapool embeds them; otherwise use export.
      const fromJob = extractPreviewItems(lastJob);
      if (fromJob.length > 0) {
        setFullDetailItems(fromJob);
        setFullDetailSource('job');
      } else {
        const exportRows = await getManapoolBulkPriceExportRows(id);
        const fromExport = extractPreviewItems({ preview: exportRows.items });
        if (fromExport.length === 0) {
          throw new Error(
            `Dry-run job ${id} completed but no item rows were found on the job or export`,
          );
        }
        setFullDetailItems(fromExport);
        setFullDetailSource('export');
      }
      setPhase('preview_ready');
    } catch (err) {
      setError(formatApiError(err));
      setPhase('error');
    }
  }, []);

  const runApplyAndSync = useCallback(async () => {
    if (!preview) return;
    setPhase('applying');
    setError(null);
    setSyncResult(null);
    try {
      const payload = (preview.request || {}) as {
        filters?: Record<string, unknown>;
        pricing?: Record<string, unknown>;
        excludeLetterShippingDisabledSellers?: boolean;
      };
      const applyResult = await applyManapoolBulkPrice({
        preview_id: preview.preview_id,
        confirm: true,
        filters: payload.filters,
        pricing: payload.pricing,
        excludeLetterShippingDisabledSellers: payload.excludeLetterShippingDisabledSellers,
      });
      const id = extractManapoolBulkJobId(applyResult);
      if (!id) {
        throw new Error('Manapool apply succeeded but returned no jobId');
      }
      setJobId(id);
      setPhase('polling');

      let status = '';
      for (let i = 0; i < MAX_POLLS; i += 1) {
        const jobPayload = await getManapoolBulkPriceJob(id);
        status = extractManapoolBulkJobStatus(jobPayload);
        setJobStatus(status);
        if (isManapoolBulkJobCompleted(status)) break;
        if (isManapoolBulkJobFailed(status)) {
          throw new Error(`Manapool bulk-price job failed with status ${status || 'unknown'}`);
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_MS));
      }
      if (!isManapoolBulkJobCompleted(status)) {
        throw new Error(
          `Timed out waiting for Manapool job ${id} (last status: ${status || 'unknown'})`,
        );
      }

      setPhase('syncing');
      const stamped = await syncManapoolBulkPriceFromExport(id, true);
      setSyncResult(stamped);
      setPhase('done');
    } catch (err) {
      setError(formatApiError(err));
      setPhase('error');
    }
  }, [preview]);

  const busy =
    phase === 'loading_defaults' ||
    phase === 'previewing' ||
    phase === 'loading_full_detail' ||
    phase === 'applying' ||
    phase === 'polling' ||
    phase === 'syncing';

  const styles = {
    section: {
      marginBottom: spacing['2xl'],
    },
    sectionTitle: {
      fontSize: typography.h2.fontSize,
      fontWeight: typography.h2.fontWeight,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    helper: {
      fontSize: typography.small.fontSize,
      color: colors.textSecondary,
      marginBottom: spacing.md,
      maxWidth: '720px',
    },
    card: {
      border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.md,
      padding: spacing.lg,
      backgroundColor: colors.surface,
    },
    meta: {
      fontSize: typography.small.fontSize,
      color: colors.textSecondary,
      marginBottom: spacing.md,
      whiteSpace: 'pre-wrap' as const,
    },
    actions: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: spacing.sm,
      marginBottom: spacing.md,
    },
    button: {
      padding: `${spacing.sm} ${spacing.md}`,
      backgroundColor: colors.primary,
      color: colors.white,
      border: 'none',
      borderRadius: borderRadius.sm,
      cursor: 'pointer',
      fontSize: typography.body.fontSize,
      fontWeight: typographyFlat.fontWeight.medium,
    },
    buttonSecondary: {
      backgroundColor: colors.surface,
      color: colors.text,
      border: `1px solid ${colors.border}`,
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    error: {
      marginTop: spacing.md,
      padding: spacing.md,
      backgroundColor: colors.errorLight,
      color: colors.error,
      borderRadius: borderRadius.sm,
      fontSize: typography.small.fontSize,
      whiteSpace: 'pre-wrap' as const,
    },
    result: {
      marginTop: spacing.md,
      padding: spacing.md,
      backgroundColor: colors.gray100,
      color: colors.text,
      borderRadius: borderRadius.sm,
      fontSize: typography.small.fontSize,
      whiteSpace: 'pre-wrap' as const,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    },
    steps: {
      fontSize: typography.small.fontSize,
      color: colors.textSecondary,
      margin: 0,
      paddingLeft: spacing.lg,
    },
    previewBlock: {
      marginTop: spacing.md,
    },
    previewHeader: {
      fontSize: typography.body.fontSize,
      fontWeight: typographyFlat.fontWeight.medium,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    previewMeta: {
      fontSize: typography.caption.fontSize,
      color: colors.textSecondary,
      marginBottom: spacing.md,
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
      gap: spacing.sm,
      marginBottom: spacing.md,
    },
    stat: {
      border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.sm,
      padding: spacing.sm,
      backgroundColor: colors.background,
    },
    statLabel: {
      fontSize: typography.caption.fontSize,
      color: colors.textSecondary,
      marginBottom: spacing.xs,
    },
    statValue: {
      fontSize: typography.body.fontSize,
      fontWeight: typographyFlat.fontWeight.semibold,
      color: colors.text,
    },
    highlight: {
      fontSize: typography.small.fontSize,
      color: colors.textSecondary,
      marginBottom: spacing.md,
      lineHeight: 1.5,
    },
    tableWrap: {
      overflowX: 'auto' as const,
      border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.sm,
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      fontSize: typography.small.fontSize,
    },
    th: {
      textAlign: 'left' as const,
      padding: `${spacing.xs} ${spacing.sm}`,
      borderBottom: `1px solid ${colors.border}`,
      color: colors.textSecondary,
      fontWeight: typographyFlat.fontWeight.medium,
      whiteSpace: 'nowrap' as const,
      backgroundColor: colors.gray100,
    },
    td: {
      padding: `${spacing.xs} ${spacing.sm}`,
      borderBottom: `1px solid ${colors.border}`,
      color: colors.text,
      verticalAlign: 'top' as const,
    },
    tdRight: {
      textAlign: 'right' as const,
      fontVariantNumeric: 'tabular-nums' as const,
      whiteSpace: 'nowrap' as const,
    },
  };

  const renderMoverRow = (item: BulkPricePreviewItem) => (
    <tr key={item.inventoryId || `${itemLabel(item)}-${item.afterPriceCents}`}>
      <td style={styles.td}>{itemLabel(item)}</td>
      <td style={{ ...styles.td, ...styles.tdRight }}>{formatCents(item.beforePriceCents)}</td>
      <td style={{ ...styles.td, ...styles.tdRight }}>{formatCents(item.afterPriceCents)}</td>
      <td
        style={{
          ...styles.td,
          ...styles.tdRight,
          color: changeColor(item.changeCents),
          fontWeight: typographyFlat.fontWeight.medium,
        }}
      >
        {formatSignedCents(item.changeCents)}
      </td>
      <td style={{ ...styles.td, ...styles.tdRight }}>{formatCents(item.marketLowCents)}</td>
      <td style={{ ...styles.td, ...styles.tdRight }}>{formatCents(item.marketPriceCents)}</td>
    </tr>
  );

  return (
    <div style={styles.section} data-testid="manapool-bulk-reprice-panel">
      <div style={styles.sectionTitle}>Manapool bulk reprice</div>
      <p style={styles.helper}>
        Optional Manapool seller-tool reprice: preview, confirm apply, then stamp listing prices
        for intelligence. Catalog sell-base is <strong>Preview Policy Delta</strong> /{' '}
        <strong>Apply Preview</strong> above — this
        tool is not the SyncBase sell driver. Does not overwrite preferred or policy prices.
      </p>

      <div style={styles.card}>
        {defaults && (
          <div style={styles.meta}>
            {`Defaults: ${String(defaults.ui_mapping.basis ?? 'Higher of Low Listed & Market Price')}`}
            {'\n'}
            {`Adjustment: ${String(defaults.ui_mapping.adjustment ?? 'Fixed Amount')} = ${String(
              defaults.ui_mapping.fixed_cent_adjustment ?? 0,
            )}¢`}
            {'\n'}
            {`Scope: ${String(defaults.ui_mapping.scope ?? 'Full Catalog')}`}
            {defaults.admin_enforcement_enabled ? '\nAdmin role required' : ''}
          </div>
        )}

        <ol style={styles.steps}>
          <li>Preview (sample + Manapool statistics + redeemable preview_id)</li>
          <li>Optional: load full item list via isPreview dry-run job</li>
          <li>Confirm apply on Manapool → poll → sync-from-export → other-channel price fan-out</li>
        </ol>

        <div style={{ ...styles.actions, marginTop: spacing.md }}>
          <button
            type="button"
            style={{
              ...styles.button,
              ...(busy ? styles.buttonDisabled : {}),
            }}
            disabled={busy}
            onClick={() => void runPreview()}
          >
            {phase === 'previewing' ? 'Previewing…' : '1. Preview'}
          </button>
          <button
            type="button"
            style={{
              ...styles.button,
              ...styles.buttonSecondary,
              ...(busy || !preview ? styles.buttonDisabled : {}),
            }}
            disabled={busy || !preview}
            onClick={() => void runLoadFullDetail()}
          >
            {phase === 'loading_full_detail'
              ? 'Loading full list…'
              : '1b. Load all items (dry-run)'}
          </button>
          <button
            type="button"
            style={{
              ...styles.button,
              ...(busy || phase !== 'preview_ready' ? styles.buttonDisabled : {}),
            }}
            disabled={busy || phase !== 'preview_ready'}
            onClick={() => void runApplyAndSync()}
          >
            {phase === 'applying' || phase === 'polling' || phase === 'syncing'
              ? 'Running…'
              : '2. Confirm apply + sync to SyncBase / Shopify'}
          </button>
          {(phase === 'done' || phase === 'error' || phase === 'preview_ready') && (
            <button
              type="button"
              style={{ ...styles.button, ...styles.buttonSecondary }}
              disabled={busy}
              onClick={() => {
                setError(null);
                setPreview(null);
                setFullDetailItems(null);
                setFullDetailJobId(null);
                setFullDetailSource('sample');
                setJobId(null);
                setJobStatus('');
                setSyncResult(null);
                setPhase('idle');
              }}
            >
              Reset
            </button>
          )}
        </div>

        {preview && previewSummary && (
          <div style={styles.previewBlock} data-testid="bulk-price-preview-summary">
            <div style={styles.previewHeader}>Preview blast radius</div>
            <div style={styles.previewMeta}>
              Expires {formatInstant(preview.expires_at)} · preview_id{' '}
              <code>{preview.preview_id.slice(0, 8)}…</code>
            </div>

            <div style={styles.statsGrid}>
              <div style={styles.stat}>
                <div style={styles.statLabel}>
                  {fullDetailItems ? 'Items loaded' : 'Items in sample'}
                </div>
                <div style={styles.statValue}>
                  {fullDetailItems
                    ? previewSummary.total
                    : (previewMeta?.previewCount ?? previewSummary.total)}
                </div>
              </div>
              <div style={styles.stat}>
                <div style={styles.statLabel}>Total in scope</div>
                <div style={styles.statValue}>
                  {previewMeta?.totalCount ?? previewSummary.total}
                </div>
              </div>
              <div style={styles.stat}>
                <div style={styles.statLabel}>Price increases</div>
                <div style={{ ...styles.statValue, color: colors.success }}>
                  {previewMeta?.increases ?? previewSummary.increases}
                </div>
              </div>
              <div style={styles.stat}>
                <div style={styles.statLabel}>Price decreases</div>
                <div style={{ ...styles.statValue, color: colors.error }}>
                  {previewMeta?.decreases ?? previewSummary.decreases}
                </div>
              </div>
              <div style={styles.stat}>
                <div style={styles.statLabel}>Unchanged / skipped</div>
                <div style={styles.statValue}>
                  {previewMeta?.unchanged ?? previewSummary.unchanged} /{' '}
                  {previewMeta?.skippedItems ?? previewSummary.skipped}
                </div>
              </div>
              <div style={styles.stat}>
                <div style={styles.statLabel}>
                  {fullDetailItems ? 'Avg |Δ| when changed' : 'Net $ (full scope)'}
                </div>
                <div
                  style={{
                    ...styles.statValue,
                    color: fullDetailItems
                      ? colors.text
                      : changeColor(previewMeta?.totalValueChangeCents ?? 0),
                  }}
                >
                  {fullDetailItems
                    ? formatCents(previewSummary.avgAbsChangeCents)
                    : formatSignedCents(
                        previewMeta?.totalValueChangeCents ??
                          previewSummary.totalIncreaseCents +
                            previewSummary.totalDecreaseCents,
                      )}
                </div>
              </div>
            </div>

            <div style={styles.highlight}>
              {previewSummary.maxIncrease && (
                <div>
                  Largest increase:{' '}
                  <strong>{itemLabel(previewSummary.maxIncrease)}</strong>{' '}
                  {formatCents(previewSummary.maxIncrease.beforePriceCents)} →{' '}
                  {formatCents(previewSummary.maxIncrease.afterPriceCents)} (
                  {formatSignedCents(previewSummary.maxIncrease.changeCents)})
                </div>
              )}
              {previewSummary.maxDecrease && (
                <div>
                  Largest decrease:{' '}
                  <strong>{itemLabel(previewSummary.maxDecrease)}</strong>{' '}
                  {formatCents(previewSummary.maxDecrease.beforePriceCents)} →{' '}
                  {formatCents(previewSummary.maxDecrease.afterPriceCents)} (
                  {formatSignedCents(previewSummary.maxDecrease.changeCents)})
                </div>
              )}
              {previewSummary.anomalyCount > 0 && (
                <div>
                  {previewSummary.anomalyCount} item
                  {previewSummary.anomalyCount === 1 ? '' : 's'} flagged with elevated anomaly
                  ratio — review top movers before confirm.
                </div>
              )}
            </div>

            <div style={styles.previewHeader}>
              {fullDetailItems
                ? `All changes by |Δ| (${movers.length}${
                    previewSummary.total > movers.length
                      ? ` changed of ${previewSummary.total} rows`
                      : ''
                  })`
                : `Sample changes by |Δ| (${movers.length}${
                    previewSummary.total > movers.length
                      ? ` changed of ${previewSummary.total} rows`
                      : ''
                  })`}
            </div>
            {!fullDetailItems &&
              previewMeta &&
              ((previewMeta.totalCount != null &&
                previewMeta.totalCount > previewSummary.total) ||
                (previewMeta.previewCount != null &&
                  previewMeta.previewCount !== previewSummary.total)) && (
                <div style={styles.highlight}>
                  Manapool <code>/preview</code> returned a <strong>sample</strong> of{' '}
                  {previewMeta.previewCount ?? previewSummary.total} row
                  {(previewMeta.previewCount ?? previewSummary.total) === 1 ? '' : 's'}
                  {previewMeta.totalCount != null
                    ? ` (statistics cover ${previewMeta.totalCount} items in scope)`
                    : ''}
                  . Use <strong>Load all items (dry-run)</strong> — Manapool{' '}
                  <code>isPreview: true</code> job → poll → full report — for the complete
                  list (same path as the seller UI).
                </div>
              )}
            {fullDetailItems && (
              <div style={styles.highlight}>
                Full list from dry-run job{' '}
                <code>{(fullDetailJobId || '').slice(0, 8)}…</code> via {fullDetailSource}
                {fullDetailSource === 'export' ? ' (job status had no embedded rows)' : ''}.
                Prices were not changed on Manapool.
              </div>
            )}
            {movers.length === 0 ? (
              <div style={styles.highlight}>No price changes in this preview.</div>
            ) : (
              <div style={{ ...styles.tableWrap, maxHeight: 480, overflowY: 'auto' as const }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Card</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>Before</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>After</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>Δ</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>Mkt low</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>Mkt</th>
                    </tr>
                  </thead>
                  <tbody>{movers.map(renderMoverRow)}</tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {preview && !previewSummary && (
          <div style={styles.previewBlock}>
            <div style={styles.previewHeader}>Preview accepted — could not parse item rows</div>
            <div style={styles.highlight}>
              Expires {formatInstant(preview.expires_at)}. You can still confirm apply.
              If you still see a raw JSON dump titled like{' '}
              <code>preview_id: …</code>, hard-refresh (Ctrl+Shift+R) — the old bundle is cached.
              Keys present:{' '}
              {preview.preview && typeof preview.preview === 'object'
                ? Object.keys(preview.preview as object).slice(0, 12).join(', ') || '(empty object)'
                : typeof preview.preview}
            </div>
            <pre style={styles.result}>{JSON.stringify(preview.preview, null, 2).slice(0, 800)}</pre>
          </div>
        )}

        {(jobId || jobStatus) && (
          <pre style={styles.result}>
            {`job_id: ${jobId || '—'}\nstatus: ${jobStatus || phase}`}
          </pre>
        )}

        {syncResult && (
          <pre style={styles.result}>
            {[
              `Export sync @ job ${syncResult.job_id}`,
              `format ${syncResult.export_format ?? '—'}`,
              `rows parsed ${syncResult.rows_parsed ?? 0}, considered ${syncResult.rows_considered ?? 0}`,
              `copies ${syncResult.copies_updated ?? 0}, variants ${syncResult.variants_updated ?? 0}`,
              `skipped ${syncResult.skipped ?? 0}, unmatched ${syncResult.unmatched ?? 0}`,
              `Shopify events emitted ${syncResult.shopify_events_emitted ?? 0} → ${(
                syncResult.shopify_fanout_targets || []
              ).join(', ') || 'none'}`,
              `preferred_price unchanged: ${syncResult.preferred_price_unchanged !== false}`,
              ...(syncResult.errors?.length
                ? [`errors: ${syncResult.errors.slice(0, 5).join('; ')}`]
                : []),
            ].join('\n')}
          </pre>
        )}

        {error && <div style={styles.error}>{error}</div>}
      </div>
    </div>
  );
}
